import { WIDGET_EVENTS, type WidgetEvent } from "@cos/analytics/events";

export { WIDGET_EVENTS, type WidgetEvent };

/** What a relayed event may carry — mirrors `analyticsTrackSchema` in packages/shared. */
export type WidgetAnalyticsProperties = Record<string, string | number | boolean>;

export interface WidgetAnalytics {
  track: (name: WidgetEvent, properties?: WidgetAnalyticsProperties) => void;
}

/**
 * The widget's analytics, which is a single socket frame.
 *
 * There is no SDK here on purpose. The widget runs inside a client's page,
 * where a third-party capture request is something their CSP is entitled to
 * refuse and their users are entitled to block — and where the bundle has
 * 45KB gzipped to spend on the whole product. So the browser reports only
 * what it alone can see (a launcher being opened, a quick reply being
 * clicked) and the API attaches the identity and forwards it, from a
 * session whose org, bot and visitor it already knows.
 *
 * Sends are fire-and-forget through the same queue every other frame uses:
 * an event raised before the socket is open goes out when it opens, and one
 * raised while it is down is dropped rather than retried forever. Losing an
 * analytics event must never be visible to a shopper.
 */
export function createWidgetAnalytics(send: (type: string, data: unknown) => void): WidgetAnalytics {
  return {
    track: (name, properties = {}) => send("analytics.track", { name, properties }),
  };
}
